import type {
  BackfillApplyPlan,
  BackfillApplyPlanRow,
  BackfillApplyReconAction,
  BackfillApplyRun,
  BackfillApplyRunResult,
} from '@/services/cloverBackfillApply'
import { formatBackfillAccountingMoney } from './backfillAuditTableConfig'

export type PlanDecisionDriftAuditRow = {
  store_name: string
  report_date: string
  decision: string
}

export function runBelongsToMonth(
  run: { report_month: string } | null | undefined,
  month: string | null | undefined,
): boolean {
  if (!run?.report_month || !month) return false
  return run.report_month === month
}

export function formatBackfillSummaryMetricCount(value: number): string {
  return value === 0 ? '—' : String(value)
}

export function formatBackfillGiftCardDeltaMetric(value: number): string {
  if (!Number.isFinite(value) || value === 0) return '—'
  return formatBackfillAccountingMoney(value)
}

export const LEAVE_ALONE_BLOCKER_REASONS = new Set([
  'skip',
  'done_by_hand',
  'applied',
  'identical',
  'identical_skipped',
  'auto_identical',
  'skipped',
  'nothing_to_do',
])

export function normalizeBlockerReason(reason: string): string {
  return reason.trim().toLowerCase().replace(/\s+/g, '_')
}

export function isLeaveAloneBlocker(reason: string): boolean {
  return LEAVE_ALONE_BLOCKER_REASONS.has(normalizeBlockerReason(reason))
}

export function isReconDeletionBlocker(reason: string): boolean {
  if (isLeaveAloneBlocker(reason)) return false
  const normalized = normalizeBlockerReason(reason)
  return (
    normalized === 'undecided' ||
    normalized === 'needs_code_change' ||
    normalized === 'by_hand' ||
    normalized.startsWith('stale') ||
    normalized === 'rebuild_failed' ||
    normalized === 'missing_in_qbo' ||
    normalized === 'duplicate_in_qbo'
  )
}

export function isPlanFresh(
  run: Pick<BackfillApplyRun, 'created_at' | 'started_at' | 'plan'>,
): boolean {
  if (run.plan) return true
  return Boolean(run.started_at ?? run.created_at)
}

export function hasBlockingBlockers(plan: BackfillApplyPlan): boolean {
  return (plan.blockers ?? []).some((blocker) => !isLeaveAloneBlocker(blocker.reason))
}

export function liveRunIsOpen(
  run: Pick<BackfillApplyRun, 'status'> | null,
): boolean {
  return run?.status === 'running' || run?.status === 'waiting_for_clover_tokens'
}

export function planEligibleRows(plan: BackfillApplyPlan): BackfillApplyPlanRow[] {
  return plan.eligible ?? plan.rows ?? []
}

export function planDecisionDrift(
  plan: BackfillApplyPlan,
  rows: PlanDecisionDriftAuditRow[],
): BackfillApplyPlanRow[] {
  const eligible = planEligibleRows(plan)
  const rowMap = new Map(rows.map((row) => [`${row.store_name}|${row.report_date}`, row]))
  const drifted: BackfillApplyPlanRow[] = []
  for (const item of eligible) {
    const auditRow = rowMap.get(`${item.store}|${item.date}`)
    if (!auditRow || auditRow.decision !== item.plan_decision) {
      drifted.push(item)
    }
  }
  return drifted
}

export function applyButtonEnabled(input: {
  dryRun: BackfillApplyRun | null
  selectedMonth: string | null
  now?: Date
  auditRows?: PlanDecisionDriftAuditRow[]
}): boolean {
  const { dryRun, selectedMonth, auditRows } = input
  if (!dryRun || !selectedMonth || dryRun.report_month !== selectedMonth) return false
  if (dryRun.mode !== 'dry_run' || !dryRun.plan) return false
  if (planEligibleRows(dryRun.plan).length === 0) return false
  if (auditRows !== undefined && planDecisionDrift(dryRun.plan, auditRows).length > 0) {
    return false
  }
  // Recon-deletion blockers (undecided / by_hand / stale / …) stay amber on
  // the card and do not disable Apply — the plug simply will not be deleted.
  return true
}

export function confirmationMatches(typed: string, reportMonth: string): boolean {
  return typed.trim() === reportMonth
}

export function countAutoDiffersRows(
  rows: Array<{ decision: string; status: string }>,
): number {
  return rows.filter((row) => row.decision === 'auto' && row.status === 'differs').length
}

export function planActionCounts(plan: BackfillApplyPlan): {
  update: number
  adopt: number
  skip: number
} {
  const update = plan.rows.filter((row) => row.path === 'update_existing_draft').length
  const adopt = plan.rows.filter((row) => row.path === 'adopt').length
  const skip = (plan.skipped ?? []).length
  return { update, adopt, skip }
}

export function tieOutPerStore(
  tieOut: BackfillApplyPlan['tie_out_expected_after'],
): Record<string, number | string> {
  if (!tieOut || typeof tieOut !== 'object') return {}
  if ('by_store' in tieOut && tieOut.by_store && typeof tieOut.by_store === 'object') {
    return tieOut.by_store
  }
  if ('per_store' in tieOut && tieOut.per_store && typeof tieOut.per_store === 'object') {
    return tieOut.per_store
  }
  return tieOut as Record<string, number | string>
}

export function tieOutVarianceBefore(
  tieOut: BackfillApplyPlan['tie_out_expected_after'],
): Record<string, number | string> {
  if (!tieOut?.variance_before || typeof tieOut.variance_before !== 'object') return {}
  return tieOut.variance_before
}

export function reconActionLabel(action: BackfillApplyReconAction): string {
  if (action === 'delete') return 'will be deleted after a successful apply'
  if (action === 'none_needed') {
    return 'No recon plug for this month (already removed / never existed)'
  }
  return 'will NOT be deleted'
}

export function reconActionTone(action: BackfillApplyReconAction): 'muted' | 'amber' | 'normal' {
  if (action === 'none_needed') return 'muted'
  if (action === 'blocked') return 'amber'
  return 'normal'
}

export function showLiveReconJeDetails(plan: BackfillApplyPlan): boolean {
  const action = plan.recon_action ?? (plan.recon_deletable ? 'delete' : 'none_needed')
  if (action === 'none_needed' || plan.recon_je?.inactive_in_qbo) return false
  return action === 'delete' || action === 'blocked'
}

export function confirmModalReconLine(plan: BackfillApplyPlan): string {
  const action = plan.recon_action ?? (plan.recon_deletable ? 'delete' : 'blocked')
  if (action === 'delete') return 'Recon JE to delete: yes'
  if (action === 'none_needed') {
    return 'Recon JE to delete: no (already removed / never existed)'
  }
  const reason = plan.recon_not_deletable_reason ?? 'open rows remain in this month'
  return `Recon JE to delete: no (${reason})`
}

export function isNothingToApply(input: {
  plan: BackfillApplyPlan | null
  differsCount: number
}): boolean {
  const { plan, differsCount } = input
  if (differsCount > 0) return false
  if (!plan) return differsCount === 0
  const action = plan.recon_action ?? (plan.recon_deletable ? 'delete' : 'none_needed')
  return plan.rows.length === 0 && action === 'none_needed'
}

export function runTieOutStores(run: BackfillApplyRun): {
  expected: Record<string, string | number>
  actual: Record<string, string | number>
  monthTies: boolean
} {
  const tieOut = run.tie_out ?? {}
  const result = run.result ?? {}
  const expected =
    (tieOut.expected as Record<string, string | number> | undefined) ??
    (result.expected as Record<string, string | number> | undefined) ??
    {}
  const actual =
    (tieOut.actual as Record<string, string | number> | undefined) ??
    (result.actual as Record<string, string | number> | undefined) ??
    {}
  const monthTies = Boolean(
    tieOut.month_ties ?? result.month_ties ?? false,
  )
  return { expected, actual, monthTies }
}

export function applyActionLabel(input: {
  dryRun: BackfillApplyRun | null
  reportMonth: string | null
  now?: Date
}): 'Dry run' | 'Apply to QBO' {
  const { dryRun, reportMonth } = input
  if (!dryRun || !reportMonth || dryRun.report_month !== reportMonth) return 'Dry run'
  if (dryRun.mode !== 'dry_run' || !dryRun.plan) return 'Dry run'
  if (hasBlockingBlockers(dryRun.plan)) return 'Dry run'
  return 'Apply to QBO'
}

const FAILED_ROW_STATUSES = new Set(['failed', 'stale_qbo', 'row_missing'])

export function parseApplyRunResult(
  result: BackfillApplyRun['result'] | Record<string, unknown> | null | undefined,
): BackfillApplyRunResult | null {
  if (!result || typeof result !== 'object') return null
  return result as BackfillApplyRunResult
}

export function isLiveRunDetail(
  run: Pick<BackfillApplyRun, 'result' | 'tie_out' | 'plan'> | null | undefined,
): boolean {
  if (!run) return false
  return run.result != null || run.tie_out != null || run.plan != null
}

export function applyRunErrorDetails(
  run: Pick<BackfillApplyRun, 'result' | 'tie_out' | 'plan'> | null | undefined,
): {
  failedRows: Array<{ store: string; date: string; error: string }>
  residuals: string[]
  reconNote: string | null
  applied: number | null
  failed: number | null
  skipped: number | null
  expected: Record<string, string | number>
  actual: Record<string, string | number>
  tieOutError: string | null
  resultError: string | null
  hasResult: boolean
} {
  const empty = {
    failedRows: [] as Array<{ store: string; date: string; error: string }>,
    residuals: [] as string[],
    reconNote: null as string | null,
    applied: null as number | null,
    failed: null as number | null,
    skipped: null as number | null,
    expected: {} as Record<string, string | number>,
    actual: {} as Record<string, string | number>,
    tieOutError: null as string | null,
    resultError: null as string | null,
    hasResult: false,
  }
  if (!run) return empty

  const parsed = parseApplyRunResult(run.result)
  if (!parsed) {
    const tieOut = run.tie_out ?? {}
    const tieOutError =
      typeof tieOut.error === 'string' && tieOut.error.trim() ? tieOut.error.trim() : null
    return {
      ...empty,
      tieOutError,
      hasResult: false,
    }
  }

  const failedRows = (parsed.rows ?? [])
    .filter((row) => {
      if (row.error) return true
      const status = row.status?.trim().toLowerCase()
      return status ? FAILED_ROW_STATUSES.has(status) : false
    })
    .map((row) => ({
      store: row.store,
      date: row.date,
      error: row.error ?? row.status ?? 'failed',
    }))

  const residuals = parsed.residuals ?? []
  const tieOut = run.tie_out ?? {}
  const expected =
    (tieOut.expected as Record<string, string | number> | undefined) ??
    parsed.expected ??
    {}
  const actual =
    (tieOut.actual as Record<string, string | number> | undefined) ??
    parsed.actual ??
    {}
  const tieOutError =
    typeof tieOut.error === 'string' && tieOut.error.trim() ? tieOut.error.trim() : null
  const resultError =
    typeof (parsed as Record<string, unknown>).error === 'string'
      ? String((parsed as Record<string, unknown>).error)
      : typeof (parsed as Record<string, unknown>).aborted === 'string'
        ? String((parsed as Record<string, unknown>).aborted)
        : typeof (parsed as Record<string, unknown>).plan_stale === 'string'
          ? String((parsed as Record<string, unknown>).plan_stale)
          : null

  let reconNote: string | null = null
  const recon = parsed.recon
  if (recon?.action === 'delete' && !recon.deleted) {
    reconNote = recon.reason
      ? `Recon plug was not deleted: ${recon.reason}`
      : 'Recon plug was not deleted.'
  } else if (recon?.action === 'none_needed') {
    reconNote = 'No recon plug to delete (already removed or never existed).'
  } else if (recon?.action === 'delete' && recon.deleted) {
    reconNote = 'Recon plug deleted.'
  } else if (recon?.action === 'blocked') {
    reconNote = 'Recon plug was not deleted (blocked by open rows or plan).'
  }

  return {
    failedRows,
    residuals,
    reconNote,
    applied: parsed.applied ?? null,
    failed: parsed.failed ?? null,
    skipped: parsed.skipped ?? null,
    expected,
    actual,
    tieOutError,
    resultError,
    hasResult: true,
  }
}

function tieOutMismatchStores(
  expected: Record<string, string | number>,
  actual: Record<string, string | number>,
): string[] {
  const stores = new Set([...Object.keys(expected), ...Object.keys(actual)])
  const mismatched: string[] = []
  for (const store of stores) {
    const exp = expected[store]
    const act = actual[store]
    if (exp === undefined && act === undefined) continue
    if (String(exp ?? '') !== String(act ?? '')) {
      mismatched.push(store)
    }
  }
  return mismatched
}

export function applyCompletedBanner(
  run: Pick<BackfillApplyRun, 'status' | 'result' | 'tie_out' | 'plan'> | null,
): { tone: 'warning'; message: string } | null {
  if (
    !run ||
    (run.status !== 'completed_with_errors' &&
      run.status !== 'failed' &&
      run.status !== 'aborted')
  ) {
    return null
  }

  const details = applyRunErrorDetails(run)
  const failedCount = details.failed ?? details.failedRows.length
  const parts: string[] = []

  if (!details.hasResult) {
    return {
      tone: 'warning',
      message: `Apply finished with errors. Run details have not loaded (status only: ${run.status}).`,
    }
  }

  if (failedCount > 0) {
    let message = `Apply finished with ${failedCount} day(s) failed.`
    const first = details.failedRows[0]
    if (first?.error) {
      const snippet =
        first.error.length > 120 ? `${first.error.slice(0, 117)}…` : first.error
      message += ` ${first.store} · ${first.date}: ${snippet}`
    }
    parts.push(message)
  }

  if (details.residuals.length > 0) {
    parts.push(
      `Apply finished but gift-card tie-out did not match expected for: ${details.residuals.join(', ')}.`,
    )
  }

  const mismatched = tieOutMismatchStores(details.expected, details.actual)
  if (mismatched.length > 0 && details.residuals.length === 0) {
    parts.push(
      `Per-store tie-out mismatch for: ${mismatched.join(', ')}.`,
    )
  }

  if (details.tieOutError) {
    parts.push(`Tie-out error: ${details.tieOutError}`)
  }

  if (details.resultError) {
    parts.push(details.resultError)
  }

  const planReconAction =
    run.plan?.recon_action ?? (run.plan?.recon_deletable ? 'delete' : 'none_needed')
  const recon = parseApplyRunResult(run.result)?.recon
  const deleteIntended = recon?.action === 'delete' || planReconAction === 'delete'
  if (deleteIntended && recon && !recon.deleted) {
    parts.push('The recon plug was not deleted.')
  }

  if (parts.length === 0) {
    parts.push(
      `Apply finished with errors (status: ${run.status}). Review counts and tie-out below.`,
    )
  }

  return { tone: 'warning', message: parts.join(' ') }
}

export type ApplyRunResultTone = 'success' | 'warning' | 'danger' | 'neutral'

export function applyRunResultHumanStatus(status: BackfillApplyRun['status']): string {
  return status.replace(/_/g, ' ')
}

export function applyRunResultTone(
  run: Pick<BackfillApplyRun, 'status' | 'result' | 'tie_out' | 'plan'> | null,
): ApplyRunResultTone {
  if (!run) return 'neutral'

  const { status } = run
  if (
    status === 'running' ||
    status === 'waiting_for_clover_tokens' ||
    status === 'planned'
  ) {
    return 'neutral'
  }

  const details = applyRunErrorDetails(run)
  const failedCount = details.failed ?? details.failedRows.length
  if (
    status === 'failed' ||
    failedCount > 0 ||
    details.resultError ||
    details.tieOutError
  ) {
    return 'danger'
  }

  if (
    status === 'completed_with_errors' ||
    status === 'aborted' ||
    applyCompletedBanner(run)
  ) {
    return 'warning'
  }

  if (status === 'completed') {
    return 'success'
  }

  return 'neutral'
}

export function applyRunResultBadgeLabel(
  tone: ApplyRunResultTone,
  status: BackfillApplyRun['status'],
): string | null {
  if (tone === 'success') return 'Applied successfully'
  if (tone === 'danger') return 'Apply failed'
  if (tone === 'warning') {
    return status === 'aborted' ? 'Aborted' : 'Completed with warnings'
  }
  return null
}

export function applyButtonDisabledReason(input: {
  dryRun: BackfillApplyRun | null
  selectedMonth: string | null
  now?: Date
  auditRows?: PlanDecisionDriftAuditRow[]
}): string | null {
  const { dryRun, selectedMonth, auditRows } = input
  if (!dryRun || !selectedMonth) return 'Run a dry run for this month first.'
  if (dryRun.report_month !== selectedMonth) return 'Run a dry run for this month first.'
  if (!dryRun.plan) return 'Dry run plan is still loading.'
  if (planEligibleRows(dryRun.plan).length === 0) {
    return 'Nothing to apply — the plan has no eligible rows. Run dry run again if decisions changed.'
  }
  if (auditRows !== undefined) {
    const drifted = planDecisionDrift(dryRun.plan, auditRows)
    if (drifted.length > 0) {
      return `This dry run is out of date — ${drifted.length} row(s) changed decision since it was generated. Run dry run again.`
    }
  }
  return null
}
